import * as React from 'react'
import {
  Box,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Icon,
  DialogContent,
  TextField,
  FormControlLabel,
  Switch,
  FormControl,
  Radio,
  FormLabel,
  InputLabel,
  CircularProgress,
  styled,
} from '@design/pt-ui'
import CreatableSelect from 'react-select'
import * as Lodash from 'lodash/fp'

import { Project } from '../interfaces/entities/project'
import { IEmail } from '../interfaces/entities/email'
import { MultiValue } from 'react-select'
import { toast } from 'react-toastify'

import { IProjectService } from '../interfaces/services/projects'
import { IUserService } from '../interfaces/services/user'

import { UserEmailData, User } from '../interfaces/entities/user'

import DI from '../app/di'
import Loader from './Loader'
import { Contact } from '../interfaces/entities/contact'
import { translateWithUserLocale } from '../utils/i18n'
import { stripSpecialCharacters } from '../utils/string'

const FieldRow = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
  display: 'flex',
  alignItems: 'baseline',
}))

interface IEmailDialogProps {
  project: Project
  user: User
  open?: boolean

  onEmailSent: (email: IEmail) => any
  onCloseDialog: () => any
}

type Language = 'english' | 'german'
type MessageType = 'formal' | 'non_formal'

interface IEmailDialogState {
  open: boolean
  sending: boolean
  loading: boolean
  language: Language
  type: MessageType
  touched: boolean
  errors: string[]
  email: IEmail
  emailData: UserEmailData | null
  options: {
    pmContacts: Contact[]
  }
}

const defaultState: IEmailDialogState = {
  open: false,
  sending: false,
  loading: false,
  language: 'german',
  type: 'non_formal',
  touched: false,
  errors: [],
  email: {
    to: [],
    cc: [],
    bcc: [],
    subject: '',
    content: '',
    signature: '',
  },
  emailData: null,
  options: {
    pmContacts: [],
  },
}

class EmailDialog extends React.PureComponent<
  IEmailDialogProps,
  IEmailDialogState
> {
  constructor(props: IEmailDialogProps) {
    super(props)
    this.state = { ...defaultState }
  }

  public componentDidMount = () => {
    this.getUserEmailData()
    this.getPMContacts()
  }

  public componentWillReceiveProps(nextProps: IEmailDialogProps) {
    this.setState((prevState) => {
      const emailText = this.getEmailText('german', 'non_formal')
      return {
        open: nextProps.open || false,
        email: {
          ...prevState.email,
          subject: emailText.subject,
          content: emailText.content,
          signature:
            prevState.email === null || prevState.email.signature.length === 0
              ? emailText.signature
              : prevState.email.signature,
        },
      }
    })
  }
  private projectService = () => {
    return DI.get('ProjectService') as IProjectService
  }

  private getUserEmailData = async () => {
    this.setState({ loading: true })

    const userService = DI.get('UserService') as IUserService
    const userEmailData = await userService.getUsersEmailData(
      this.props.project.client.id,
    )

    await this.setState({
      loading: false,
      emailData:
        userEmailData.type === 'success' ? userEmailData.payload : null,
    })
  }

  /**
   * Get contacts from Memoria
   */
  private getPMContacts = async () => {
    const clientResponse = await this.projectService().getClient(
      this.props.project.client.id,
      true,
      this.props.project.id,
    )
    if (clientResponse.type === 'success') {
      const clientWithContacts = clientResponse.payload
      await this.setState((state) => {
        return {
          ...state,
          options: {
            ...state.options,
            pmContacts: clientWithContacts.pmContacts,
          },
        }
      })
    } else {
      // TODO: Handle client load error
    }
  }

  private handleLanguageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    this.setState((prevState: IEmailDialogState) => {
      const language = value as Language
      const subject = prevState.touched
        ? prevState.email.subject
        : this.getEmailText(language, prevState.type).subject
      const content = prevState.touched
        ? prevState.email.content
        : this.getEmailText(language, prevState.type).content
      const signature = prevState.touched
        ? prevState.email.signature
        : this.getEmailText(language, prevState.type).signature

      return {
        ...prevState,
        language,
        email: {
          ...prevState.email,
          subject,
          content,
          signature,
        },
      }
    })
  }

  private handleMsgTypeChange = () => {
    this.setState((prevState: IEmailDialogState) => {
      const type = prevState.type === 'formal' ? 'non_formal' : 'formal'
      const subject = prevState.touched
        ? prevState.email.content
        : this.getEmailText(prevState.language, type).subject
      const content = prevState.touched
        ? prevState.email.content
        : this.getEmailText(prevState.language, type).content
      return {
        ...prevState,
        type,
        email: {
          ...prevState.email,
          subject,
          content,
        },
      }
    })
  }

  private onToChange = async (
    value: MultiValue<{ value: string; label: string }>,
  ) => {
    await this.setState((prevState: IEmailDialogState) => {
      const to = Array.isArray(value)
        ? value.map((mail: any) => mail.value)
        : []
      return {
        ...prevState,
        email: {
          ...prevState.email,
          to,
        },
      }
    })

    this.setState((prevState: IEmailDialogState) => ({
      ...prevState,
      email: {
        ...prevState.email,
        subject: prevState.touched
          ? this.state.email.subject
          : this.getEmailText(prevState.language, prevState.type).subject,
        content: prevState.touched
          ? this.state.email.content
          : this.getEmailText(prevState.language, prevState.type).content,
      },
    }))
    this.validate()
  }

  private onCCChange = (
    value: MultiValue<{ value: string; label: string }>,
  ) => {
    const cc = Array.isArray(value) ? value.map((mail: any) => mail.value) : []

    this.setState((prevState: IEmailDialogState) => ({
      ...prevState,
      email: {
        ...prevState.email,
        cc,
      },
    }))
  }

  private onBCChange = (
    value: MultiValue<{ value: string; label: string }>,
  ) => {
    const bcc = Array.isArray(value) ? value.map((mail: any) => mail.value) : []

    this.setState((prevState: IEmailDialogState) => ({
      ...prevState,
      email: {
        ...prevState.email,
        bcc,
      },
    }))
  }

  private onSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const subject = stripSpecialCharacters(e.target.value)

    this.setState((prevState: IEmailDialogState) => ({
      ...prevState,
      touched: true,
      email: {
        ...prevState.email,
        subject,
      },
    }))
    this.validate()
  }

  private onContentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const content = stripSpecialCharacters(e.target.value)

    this.setState((prevState: IEmailDialogState) => ({
      ...prevState,
      touched: true,
      email: {
        ...prevState.email,
        content,
      },
    }))
    this.validate()
  }

  private validate = () => {
    this.setState({ errors: [] })
    if (this.state.email.to.length === 0) {
      this.setState((prevState: IEmailDialogState) => ({
        ...prevState,
        errors: [
          ...prevState.errors,
          translateWithUserLocale('email_dialog.validation_recipient'),
        ],
      }))
    }
    if (this.state.email.subject.trim().length === 0) {
      this.setState((prevState: IEmailDialogState) => ({
        ...prevState,
        errors: [
          ...prevState.errors,
          translateWithUserLocale('email_dialog.validation_subject'),
        ],
      }))
    }
    if (this.state.email.content.trim().length === 0) {
      this.setState((prevState: IEmailDialogState) => ({
        ...prevState,
        errors: [
          ...prevState.errors,
          translateWithUserLocale('email_dialog.validation_content'),
        ],
      }))
    }
  }

  private handleSend = async () => {
    const linkToAdvertiser =
      this.state.emailData !== null
        ? `${this.state.emailData.hostname}/?modul=creative_pool#/project/${this.props.project.id}`
        : ''
    await this.validate()
    if (this.state.errors.length === 0) {
      this.setState({ sending: true })
      const emailResponse = await this.projectService().sendEmail(
        {
          ...this.state.email,
          content:
            linkToAdvertiser !== ''
              ? this.state.email.content.replace(
                  linkToAdvertiser,
                  `<a href="${linkToAdvertiser}">${linkToAdvertiser}</a>`,
                )
              : this.state.email.content,
        },
        this.props.project.client.id,
      )
      await this.setState({ sending: false })
      if (emailResponse.type === 'error') {
        toast.error(emailResponse.payload.message, { autoClose: false })
      } else {
        this.props.onEmailSent(this.state.email)
        toast.success(emailResponse.payload)
      }
    }
  }

  private handleClose = () => {
    this.setState((prevState) => {
      return {
        ...defaultState,
        touched: false,
        emailData: prevState.emailData,
        options: prevState.options,
      }
    })
    this.props.onCloseDialog()
  }

  private getEmailText = (language: Language, type: MessageType) => {
    const { project } = this.props

    const selectedEmails = this.state ? this.state.email.to : []

    const linkToAdvertiser =
      this.state.emailData !== null
        ? `${this.state.emailData.hostname}/?modul=creative_pool#/project/${this.props.project.id}`
        : ''

    const name = this.props.project.contacts
      .filter(
        (contact) =>
          contact.email &&
          selectedEmails.findIndex((email) => email === contact.email) !== -1,
      )
      .map((contact) => {
        let title =
          contact.title && type === 'formal' ? contact.title.toLowerCase() : ''
        switch (title) {
          case 'frau':
            title = language === 'german' ? 'Sehr geehrte Frau' : 'Dear Mrs.'
            break
          case 'herr':
            title = language === 'german' ? 'Sehr geehrter Herr' : 'Dear Mr.'
            break
        }
        return `${title} ${contact.name}, `
      })
      .join('')
      .trim()

    const sendersName = this.props.user.fullName

    const agencySignature =
      this.state.emailData !== null
        ? `
        <div>
        <img src="${
          this.state.emailData.agency.logo
        }" alt="Agency logo" style="margin-left:10px" title="${
          this.state.emailData.agency.name
        }" data-auto-embed />
          <p style="margin-left:10px;margin-top: 5px">
            <span style="font-family:Calibri,Arial;color:rgba(70,70,70);text-transform:uppercase;font-size:12pt;">
              ${this.state.emailData.agent.firstname} ${
                this.state.emailData.agent.lastname
              }
            </span>
            <span style="display:block;font-family:Arial;font-size:8pt;font-weight:bold;color:rgba(86,24,65);margin-top: 10px">
              ${this.state.emailData.agent.title}
            </span>
          </p>
          </div>
          <p style="margin-left:10px;font-family:Calibri,Arial;font-size:9pt;color:rgba(70,70,70)">
            <span>${this.state.emailData.agent.phone}</span>
            <span style="display:block">
            <a href="${this.state.emailData.agent.email}" target="_blank">
            ${this.state.emailData.agent.email}
            </a></span>
          </p>
          <p style="margin-left:10px;display:grid;font-family:Calibri,Arial;font-size:9pt;color:rgba(166,166,166)">
            <span>${this.state.emailData.agency.name}</span>
            <span>${this.state.emailData.agency.address.street}</span>
            <span>${this.state.emailData.agency.address.zip} ${
              this.state.emailData.agency.address.city
            }</span>
            <a href="${this.state.emailData.agency.url}"
              style="font-family:Arial;font-size:8pt;color:rgba(86,24,65);margin-bottom:13px"
              target="_blank">${this.state.emailData.agency.url
                .replace(/https:\/\/|http:\/\//, '')
                .replace(/\/$/, '')}
            </a>
          ${
            language === 'german' ? 'Geschäftsführer' : 'Managing Director'
          }: ${this.state.emailData.agency.financeData.managingDirectors.join(
            ' - ',
          )} · ${this.state.emailData.agency.address.city}:${
            this.state.emailData.agency.financeData.companyId
          } · USt-IdNr.: ${this.state.emailData.agency.financeData.taxId}
          </p>
          <span>
          <p style="font-family:Arial;font-size:8pt;color:rgba(166,166,166);text-decoration:none;margin-left:10px">This e-mail may contain confidential and/or privileged information. If you are not the intended recipient (or have received this e-mail in error) please notify the sender immediately and destroy this e-mail. Any unauthorized copying, disclosure or distribution of the material in this e-mail is strictly forbidden. Our privacy policy can be obtained from our website <a href="www.piamedia.com/privacy-policy/">www.piamedia.com/privacy-policy/</a></p>
        </span>
          `
        : ''

    /** EMAIL TEXT */
    const emailText = {
      german: {
        formal: {
          subject: `Projekt ${project.name} steht für Creative Uploads zur Verfügung`,
          content:
            `${name}

im Creative Pool Projekt "${project.name}" unter ${linkToAdvertiser} ist es ab heute möglich die notwendigen Creatives bis zum ` +
            project.deadline.format('DD.MM.YYYY') +
            `  hochzuladen.

Bei Fragen zögert bitte nicht uns zu kontaktieren.

Mit freundlichen Grüßen
${sendersName}
`,
          signature: agencySignature,
        },
        non_formal: {
          subject: `Projekt ${project.name} steht für Creative Uploads zur Verfügung`,
          content:
            `Hallo ${name}

im Creative Pool Projekt "${project.name}" unter ${linkToAdvertiser} ist es ab heute möglich die notwendigen Creatives bis zum ` +
            project.deadline.format('DD.MM.YYYY') +
            `  hochzuladen.

Bei Fragen zögert bitte nicht uns zu kontaktieren.

Beste Grüße,
${sendersName}
`,
          signature: agencySignature,
        },
      },
      english: {
        formal: {
          subject: `Project ${project.name} is available for creative uploads`,
          content: `${name}

the Creative Pool project "${
            project.name
          }" is now available for you to upload your creatives until the ${project.deadline.format(
            'DD.MM.YYYY',
          )} at ${linkToAdvertiser}.

In case of questions please do not hesitate to contact us.

Yours sincerely,
${sendersName}`,
          signature: agencySignature,
        },
        non_formal: {
          subject: `Project ${project.name} is available for creative uploads`,

          content: `Dear ${name}

the Creative Pool project "${
            project.name
          }" is now available for you to upload your creatives until the ${project.deadline.format(
            'DD.MM.YYYY',
          )} at ${linkToAdvertiser}.

In case of questions please do not hesitate to contact us.

Best wishes,
${sendersName}`,
          signature: agencySignature,
        },
      },
    }

    return emailText[language][type]
  }

  private userHasEmail = () => {
    if (this.state.emailData == null) {
      return false
    }

    const email = Lodash.getOr('', ['agent', 'email'], this.state.emailData)

    return email.length > 0
  }

  public render() {
    return (
      <Dialog
        disableRestoreFocus={true}
        open={this.state.open}
        onClose={this.handleClose}
        maxWidth={'md'}
        PaperProps={{ sx: { maxHeight: '100%' } }}>
        <DialogTitle>
          {translateWithUserLocale('email_dialog.send_email_to_client')}
        </DialogTitle>
        <DialogContent sx={{ width: 900 }}>
          {this.state.loading ? <Loader variant={'overlay'} /> : null}

          {this.userHasEmail() ? (
            <>
              <FieldRow>
                <InputLabel
                  sx={{
                    display: 'block',
                    marginBottom: 1,
                    marginRight: 1,
                    minWidth: 30,
                  }}>
                  To:
                </InputLabel>
                <CreatableSelect
                  className="email-select-input"
                  isMulti={true}
                  onChange={this.onToChange}
                  options={this.props.project.contacts
                    .filter((contact) => contact.email !== undefined)
                    .map((contact) => ({
                      value: contact.email || '',
                      label: contact.name,
                    }))}
                  placeholder={translateWithUserLocale(
                    'email_dialog.select_contacts',
                  )}
                />
              </FieldRow>

              <FieldRow>
                <InputLabel
                  sx={{
                    display: 'block',
                    marginBottom: 1,
                    marginRight: 1,
                    minWidth: 30,
                  }}>
                  Cc:
                </InputLabel>
                <CreatableSelect
                  className="email-select-input"
                  isMulti={true}
                  onChange={this.onCCChange}
                  options={[
                    ...this.props.project.contacts
                      .filter((contact) => contact.email !== undefined)
                      .map((contact) => ({
                        value: contact.email || '',
                        label: contact.name,
                      })),
                    ...this.props.project.pmContacts
                      .filter((contact) => contact.email !== undefined)
                      .map((contact) => ({
                        value: contact.email || '',
                        label: contact.name,
                      })),
                  ]}
                  placeholder={translateWithUserLocale(
                    'email_dialog.select_contacts',
                  )}
                />
              </FieldRow>

              <FieldRow>
                <InputLabel
                  sx={{
                    display: 'block',
                    marginBottom: 1,
                    marginRight: 1,
                    minWidth: 30,
                  }}>
                  Bcc:
                </InputLabel>
                <CreatableSelect
                  className="email-select-input"
                  isMulti={true}
                  onChange={this.onBCChange}
                  options={[
                    ...this.props.project.contacts
                      .filter((contact) => contact.email !== undefined)
                      .map((contact) => ({
                        value: contact.email || '',
                        label: contact.name,
                      })),
                    ...this.state.options.pmContacts
                      .filter((pmContact) => pmContact.email !== undefined)
                      .map((contact) => ({
                        value: contact.email || '',
                        label: contact.name,
                      })),
                  ]}
                  placeholder={translateWithUserLocale(
                    'email_dialog.select_contacts',
                  )}
                />
              </FieldRow>
              <FormControlLabel
                sx={{ marginLeft: 0, mt: 1, gap: 1.5 }}
                control={
                  <Switch
                    state={this.state.type === 'formal' ? 'on' : 'off'}
                    onChange={this.handleMsgTypeChange}
                    disabled={this.state.touched}
                  />
                }
                label={
                  this.state.type === 'formal'
                    ? translateWithUserLocale('email_dialog.formal')
                    : translateWithUserLocale('email_dialog.non_formal')
                }
              />
              <FormControl
                sx={{
                  flexFlow: 'row',
                  alignItems: 'center',
                  width: '100%',
                }}>
                <FormLabel sx={{ marginRight: 1 }}>
                  {translateWithUserLocale('language')}
                </FormLabel>
                <Box sx={{ width: '90%', display: 'flex', flexFlow: 'row' }}>
                  <FormControlLabel
                    value="english"
                    control={
                      <Radio
                        color="primary"
                        onChange={this.handleLanguageChange}
                      />
                    }
                    label={translateWithUserLocale('english')}
                    labelPlacement="end"
                    checked={this.state.language === 'english'}
                    disabled={this.state.touched}
                  />
                  <FormControlLabel
                    value="german"
                    control={
                      <Radio
                        color="primary"
                        onChange={this.handleLanguageChange}
                      />
                    }
                    label={translateWithUserLocale('german')}
                    labelPlacement="end"
                    checked={this.state.language === 'german'}
                    disabled={this.state.touched}
                  />
                </Box>
              </FormControl>
              <TextField
                sx={{ marginBottom: 1 }}
                label={translateWithUserLocale('email_dialog.subject')}
                fullWidth={true}
                onChange={this.onSubjectChange}
                value={this.state.email.subject}
              />
              <FormControl sx={{ width: '100%' }}>
                <FormLabel>
                  {translateWithUserLocale('email_dialog.content')}
                </FormLabel>
                <TextField
                  rows={10}
                  multiline={true}
                  onChange={this.onContentChange}
                  value={this.state.email.content}
                />
              </FormControl>
              <FormControl sx={{ width: '100%', marginTop: '8px' }}>
                <Box
                  sx={(theme) => ({
                    marginTop: 1,
                    borderBottom: `1px solid ${theme.palette.grey[600]}`,
                  })}
                  dangerouslySetInnerHTML={{
                    __html: this.state.email.signature,
                  }}
                />
              </FormControl>
            </>
          ) : (
            <>{translateWithUserLocale('email_dialog.mail_not_set')}</>
          )}
        </DialogContent>

        <DialogActions>
          {this.userHasEmail() ? (
            <>
              <div>{this.state.errors.join(' ')}</div>
              <Box sx={{ margin: 1, position: 'relative' }}>
                <Button
                  disabled={this.state.sending || this.state.errors.length > 0}
                  onClick={this.handleSend}
                  color="inherit">
                  <Icon>send</Icon>
                  {translateWithUserLocale('send')}
                </Button>
                {this.state.sending && (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-12px',
                      marginLeft: '-12px',
                    }}
                  />
                )}
              </Box>
            </>
          ) : null}
          <Button
            onClick={this.handleClose}
            disabled={this.state.sending}
            color="inherit">
            <Icon>close</Icon>
            {translateWithUserLocale('cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
}

export default EmailDialog
